import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Avatar,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../component/layout/Layout';
import Profile from '../component/profilePageComponent/Profile';
import AccountSecurity from '../component/profilePageComponent/AccountSecurity';
import Loader from '../component/Loader/Loader';
import { getSingleStudent } from '../helper/student_helper';

const StudentProfile = ({ studentId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loader, setLoader] = useState(false);
  const [selectedTab, setSelectedTab] = useState("profile");
  const [profileData, setProfileData] = useState(null);

  // Fetch profile data if not already done
  useEffect(() => {
    const student = JSON.parse(localStorage.getItem('student'));
    const id = student?.id || studentId;
    if (!id) return;

    // fetch only if we don't already have profileData
    if (!profileData) {
      const fetchProfile = async () => {
        try {
          setLoader(true);
          const data = await getSingleStudent(id);
          if (data?.success) setProfileData(data.result?.[0] ?? null);
          else console.error('Failed to load profile:', data?.message);
        } catch (err) {
          console.error('Error fetching student in parent:', err);
        } finally {
          setLoader(false);
        }
      };
      fetchProfile();
    }
  }, [studentId, profileData]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  const avatarInitials = `${profileData?.firstName?.[0] || ''}${profileData?.lastName?.[0] || ''}`.toUpperCase();

  if (loader) {
    return <Loader />;
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4, mt: { xs: 2, md: 5 } }}>
        <Grid container>
          {/* Sidebar */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              backgroundColor: '#1e1e2d',
              color: 'white',
              p: { xs: 2, md: 3 },
              height: { md: '100vh' },
              position: { md: 'sticky' },
              top: 0,
              overflowY: 'auto'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: { xs: 60, md: 80 },
                  height: { xs: 60, md: 80 },
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                {avatarInitials || 'U'}
              </Avatar>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {profileData?.firstName} {profileData?.lastName}
              </Typography>
              <Typography variant="body2" color="grey.300">
                Student
              </Typography>
            </Box>

            <Divider sx={{ my: 2, borderColor: 'grey.700' }} />

            <List sx={{ px: 1 }}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedTab === 'profile'}
                  onClick={() => handleTabChange('profile')}
                  sx={{
                    borderRadius: 1,
                    mx: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    },
                    '&:hover': { bgcolor: 'grey.700' }
                  }}
                >
                  <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: '1rem' }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedTab === 'accountSecurity'}
                  onClick={() => handleTabChange('accountSecurity')}
                  sx={{
                    borderRadius: 1,
                    mx: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    },
                    '&:hover': { bgcolor: 'grey.700' }
                  }}
                >
                  <ListItemText primary="Account Security" primaryTypographyProps={{ fontSize: '1rem' }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Grid>

          {/* Content Area */}
          <Grid item xs={12} md={9} sx={{ p: { xs: 2, md: 3 } }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                overflow: 'hidden',
                minHeight: { md: '100vh' }
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {selectedTab === 'profile' && (
                  <Profile setLoader={setLoader} profileData={profileData} setProfileData={setProfileData} />
                )}
                {selectedTab === 'accountSecurity' && <AccountSecurity setLoader={setLoader} />}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default StudentProfile;